import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, Inject, Input, OnInit, Optional } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { map } from 'rxjs/operators';
import { Assessment, Doctor } from 'src/API';
import { LayoutsService } from 'src/app/logged-in-navbar/clinic-setup-modal/layouts/layouts.service';
import { EyePosition } from 'src/app/patients/patient/patient-chart/patient-chart.component';
import { SchemaService } from 'src/app/shared/symptoms/services/schema/schema.service';
import { DrugFormat } from '../../medication-lookup/drug.pipe';
import { SymptomModes, SymptomTypes } from '../../symptoms/symptom.model';
import { InteractionSignOffGroup } from './../../../signature/interaction-signoff/interaction-signoff.model';
import { CustomPdfExportService } from './custom-pdf-export.service';
import { assessmentTypeKeys, customPdf } from './pdf-custom-export.model';

@Component({
  selector: 'csi-pdf-custom-export',
  templateUrl: './pdf-custom-export.component.html',
  styleUrls: ['./pdf-custom-export.component.scss']
})
export class PdfCustomExportComponent implements OnInit {
  @Input() assessmentBody: any;
  @Input() exportPdf: boolean = false;
  @Input() lang: string;
  @Input() translateObject: any;
  @Input() schemaService: SchemaService;
  @Input() riskFactors: any;
  @Input() elementColumnConfigMap: any;
  @Input() localMedicalHistoryFields: string[];
  @Input() isAtRisk: boolean = false;
  @Input() previousDryEyeForm: any;

  @Input() isAssessmentLocked: boolean;
  @Input() signingDoctor: Partial<Doctor>;

  public isExporting = false;

  public customPdfMap = {};
  public parseAssessmentBody = {};
  public sectionList = [];
  public processedData = {};
  public specialSectionMap = {
    treatmentPerformed: 'Treatment Performed',
    medicalHistory: 'Medical History',
    pieCharts: 'Pie Charts',
    patentCharts: 'Patient Charts',
    discussionSummary: 'Discussion Summary'
  };

  public interactionSignoffGroup: InteractionSignOffGroup = null;

  public readonly SymptomTypes = SymptomTypes;
  public readonly SymptomModes = SymptomModes;
  public readonly DrugFormat = DrugFormat;
  public readonly EyePosition = EyePosition;
  public mappingData: any = {};

  public selectedAssessmentFieldFormGroup = new FormGroup({});

  public assessmentType: string;

  private elementLength: number = 0;
  private layoutHistoryList = [];
  public allSectionUnchecked$: any;
  constructor(
    @Optional() public dialogRef: MatDialogRef<PdfCustomExportComponent>,
    @Optional()
    @Inject(MAT_DIALOG_DATA)
    public data: { assessmentBody: Assessment; patientCharts: any },
    public layoutsService: LayoutsService,
    public customPdfExportService: CustomPdfExportService
  ) {
    if (!this.data) {
      this.data = { assessmentBody: null, patientCharts: null };
    }
  }
  loadExportPdfData() {
    this.customPdfMap = this.customPdfExportService.customPdfData;
    this.assessmentType = this.customPdfExportService.assessmentType;

    this.localMedicalHistoryFields = Object.keys(this.customPdfMap['medicalHistory'] || []).filter(
      item => item !== 'index' && item !== 'show'
    );
  }

  loadAssessmentAndAssessmentType() {
    this.parseAssessmentBody = {
      ...JSON.parse(this.data.assessmentBody.body),
      patient: { ...this.data.patientCharts }
    };

    this.assessmentType = JSON.parse(this.data.assessmentBody.body)['dryEyeForm'][
      'assessmentMethod'
    ];
  }

  buildPdfMap() {
    this.customPdfMap = Object.entries(customPdf).reduce((acc, [key, customPdfItem]) => {
      const parentData = this.parseAssessmentBody[customPdfItem.parentName];

      if (parentData) {
        const processedEntries = this.customPdfExportService.configuration(
          customPdfItem,
          parentData,
          this.layoutsService
        );

        if (processedEntries && Object.keys(processedEntries).length > 0) {
          acc[key] = processedEntries;
          this.addFormControls(key);
        }
      }
      return acc;
    }, {});
  }

  addFormControls(key: string) {
    if (!this.selectedAssessmentFieldFormGroup.contains(key)) {
      this.selectedAssessmentFieldFormGroup.addControl(key, new FormControl(true));
    }
  }
  buildSectionList() {
    const section = (item: string) =>
      assessmentTypeKeys(this.assessmentType).some(assessmentSection => {
        return assessmentSection.toLowerCase().includes(item.toLowerCase());
      });

    return Object.keys(this.customPdfMap || []).filter(section);
  }
  ngOnInit() {
    if (this.exportPdf) {
      this.interactionSignoffGroup = this.customPdfExportService.interactionSignoffGroup;
      this.loadExportPdfData();
      return;
    }
    this.loadAssessmentAndAssessmentType();
    this.buildPdfMap();
    this.sectionList = this.buildSectionList();
    this.allSectionUnchecked$ = this.selectedAssessmentFieldFormGroup.valueChanges.pipe(
      map(item => Object.values(item).filter(value => value).length < 1)
    );
  }

  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.sectionList, event.previousIndex, event.currentIndex);
  }
  exportPDF() {
    this.isExporting = true;
    const customPdfToSend = { ...this.customPdfMap };
    let index = 0;
    this.sectionList.forEach(section => {
      const toShow = this.selectedAssessmentFieldFormGroup.controls[section].value;
      const sectionData = customPdfToSend[section];
      if (toShow) {
        customPdfToSend[section] = { ...sectionData, index: index, show: toShow };
        ++index;
        return;
      }
      delete customPdfToSend[section];
    });

    this.data.assessmentBody;
    this.customPdfExportService.customPdfData = customPdfToSend;
    this.customPdfExportService.assessmentType = this.assessmentType;
    this.customPdfExportService.interactionSignoffGroup = this.parseAssessmentBody[
      'interactionSignoffGroup'
    ];
    this.dialogRef.close(this.isExporting);
    this.isExporting = false;
  }

  sortByValue(a: any, b: any): number {
    if (a.value.show && b.value.show) {
      return a.value.index - b.value.index;
    }
  }

  parseValue(val: any) {
    if (typeof val === 'object') {
      return Object.entries(val)
        .map(([k, v]) => `${k[0].toUpperCase() + k.substring(1)}: ${v}`)
        .join(', ');
    }
    return val;
  }
}
